import React, { useEffect, useState, useRef } from 'react';
import { IDropDownListItem } from '../../../Types/DropDownList/IDropDownListItem';
// CSS
import styles from './DropDownList.module.css';

type DropDownListProps = {
  value: string | number;
  placeholder: string;
  data: IDropDownListItem[];
  readonly?: boolean;
  noOptionsMessage?: string;
  clearable?: boolean;
  onChange?: (selectedItem: IDropDownListItem) => void;
  style?: React.CSSProperties; // Optional additional styles
};

const DropDownList: React.FC<DropDownListProps> = ({
  value,
  placeholder,
  data,
  onChange,
  style,
  readonly,
  noOptionsMessage,
  clearable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IDropDownListItem | undefined>(data.find(x => x.value === value) ?? undefined);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedItem(data.find(x => x.value === value) ?? undefined);
  }, [value, data]);

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleItemClick = (item: IDropDownListItem) => {
    setSelectedItem(item);
    setIsOpen(false);
    if (onChange) {
      onChange(item);
    }
  };

  const hasSelection =
    selectedItem != null || (value !== '' && value !== null && value !== undefined);

  const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedItem(undefined);
    setIsOpen(false);
    onChange?.({
      value: '',
      text: '',
      colour: undefined,
      textColour: undefined,
    });
  };

  // Merge the passed styles with the dynamic styles
  const combinedStyles = {

    ...style, // Merge any additional styles from the props
  };

  return (
    <div className={styles.dropdown} ref={dropdownRef}>
      <div
        className={`${styles.dropbtnGroup} ${isOpen ? styles.open : ''}`}
        style={combinedStyles}
      >
        <div className={styles.mainSection}>
          <button
            type="button"
            onClick={toggleDropdown}
            className={styles.dropbtnMain}
            disabled={readonly || data.length === 0}
          >
            <span className={styles.selectedText}>
              {selectedItem ? selectedItem.text : (data.length === 0 ? (noOptionsMessage || "No options available") : placeholder)}
            </span>
          </button>
          <button
            type="button"
            onClick={toggleDropdown}
            className={styles.arrowButton}
            disabled={readonly || data.length === 0}
            aria-label="Toggle options"
          >
            <span className={styles.arrow}>▾</span>
          </button>
        </div>
        {clearable && hasSelection && !readonly && (
          <div className={styles.clearSection}>
            <button
              type="button"
              className={styles.clearButton}
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <span className={styles.clearIcon} aria-hidden="true">×</span>
            </button>
          </div>
        )}
      </div>
        {isOpen && (
        <ul className={styles.dropdownContent}>
          {data.map((item) => (
            <li
              key={item.text}
              onClick={() => handleItemClick(item)}
              className={styles.dropdownItem}
            >
              {item.text}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default DropDownList;